import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info, Lock } from 'lucide-react';
import type { ApiMQTTServer, CreateMQTTServerRequest, UpdateMQTTServerRequest } from '@/types/api';

const mqttServerSchema = z.object({
  name: z.string().min(1, 'El nombre es requerido').max(100),
  broker: z.string().min(1, 'El broker es requerido'),
  port: z.coerce.number().min(1, 'Puerto inválido').max(65535, 'Puerto inválido'),
  username: z.string().optional(),
  password: z.string().optional(),
  useTLS: z.boolean(),
});

type MQTTServerFormData = z.infer<typeof mqttServerSchema>;

interface MQTTServerFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  server?: ApiMQTTServer | null;
  onSubmit: (data: CreateMQTTServerRequest | UpdateMQTTServerRequest) => void;
  isLoading?: boolean;
}

export function MQTTServerFormDialog({
  open,
  onOpenChange,
  server,
  onSubmit,
  isLoading = false,
}: MQTTServerFormDialogProps) {
  const isEditing = !!server;

  const form = useForm<MQTTServerFormData>({
    resolver: zodResolver(mqttServerSchema),
    defaultValues: {
      name: '',
      broker: '',
      port: 1883,
      username: '',
      password: '',
      useTLS: false,
    },
  });

  // Reset form when server changes or dialog opens
  useEffect(() => {
    if (open) {
      if (server) {
        form.reset({
          name: server.name,
          broker: server.broker,
          port: server.port,
          username: server.username || '',
          password: '', // Never pre-fill password for security
          useTLS: server.useTLS,
        });
      } else {
        form.reset({
          name: '',
          broker: '',
          port: 1883,
          username: '',
          password: '',
          useTLS: false,
        });
      }
    }
  }, [open, server, form]);

  const handleSubmit = (data: MQTTServerFormData) => {
    const requestData: CreateMQTTServerRequest | UpdateMQTTServerRequest = {
      name: data.name,
      broker: data.broker,
      port: data.port,
      username: data.username || undefined,
      useTLS: data.useTLS,
    };

    // Only include password if it was provided
    if (data.password && data.password.length > 0) {
      requestData.password = data.password;
    }

    onSubmit(requestData);
  };

  const useTLS = form.watch('useTLS');

  // Auto-update port when TLS changes
  useEffect(() => {
    const currentPort = form.getValues('port');
    if (useTLS && currentPort === 1883) {
      form.setValue('port', 8883);
    } else if (!useTLS && currentPort === 8883) {
      form.setValue('port', 1883);
    }
  }, [useTLS, form]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="font-display">
            {isEditing ? 'Editar Servidor MQTT' : 'Nuevo Servidor MQTT'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Modifica la configuración del broker MQTT.'
              : 'Añade un nuevo broker MQTT para la comunicación directa con dispositivos.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nombre *</FormLabel>
                  <FormControl>
                    <Input placeholder="Gesinen GW" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="broker"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel>Broker *</FormLabel>
                    <FormControl>
                      <Input placeholder="gesinen.es" {...field} />
                    </FormControl>
                    <FormDescription className="text-xs">
                      Host del broker (sin protocolo)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="port"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Puerto *</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="1883" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Usuario</FormLabel>
                    <FormControl>
                      <Input placeholder="gesinen" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Contraseña {isEditing && '(dejar vacío para mantener)'}
                    </FormLabel>
                    <FormControl>
                      <Input type="password" placeholder="••••••••" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="useTLS"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      Usar TLS/SSL
                    </FormLabel>
                    <p className="text-sm text-muted-foreground">
                      Habilitar conexión segura con el broker (puerto 8883)
                    </p>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {isEditing && (
              <Alert className="bg-muted/50">
                <Info className="h-4 w-4" />
                <AlertDescription className="text-sm">
                  La contraseña no se muestra por seguridad. Solo ingresa una nueva si deseas cambiarla.
                </AlertDescription>
              </Alert>
            )}

            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="gradient-primary text-white"
                disabled={isLoading}
              >
                {isLoading ? 'Guardando...' : isEditing ? 'Guardar cambios' : 'Crear Servidor'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
